import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { interval, Subject, takeUntil, switchMap } from 'rxjs';
import { MessagingService } from '../../../core/services/messaging.service';
import { OrganizationService } from '../../../core/services/organization.service';

import { FormsModule } from '@angular/forms';
import { OrganizationUtilService } from '../../../core/services/organization-util.service';
import { MessageDetailComponent } from '../message-detail/message-detail.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-messages-list',
  standalone: true,
  imports: [
    FormsModule
],
  templateUrl: './messages-list.component.html',
  styleUrls: ['./messages-list.component.css']
})
export class MessagesListComponent implements OnInit, OnDestroy {
  /** Fetch read messages and their headers, filter by readAt, and update state (Angular version of getReadMessagesFirstPage) */
  getReadMessagesFirstPage(): void {
    if (this.data.readMessages && this.data.readMessages.length > 0) {
      return;
    }
    // Step 1: Fetch read messages
    this.messagingService.getReadMessages(this.orgIds, this.numberPerPage, 1)
      .pipe(takeUntil(this.destroy$))
      .subscribe(async res => {
        const rows = res?.rows || [];
        const messageIds = rows.map((row: any) => row.id);
        // Step 2: Fetch headers for these messages
        let headerResults: any = null;
        try {
          headerResults = await this.messagingService.getToOrgMessageHeaders(messageIds).toPromise();
        } catch (err) {
          // handle error
          return;
        }
        // Step 3: Attach headers and localTime
        rows.forEach((row: any) => {
          row.ToMessageHeaders = [
            (headerResults?.rows || []).find((o: any) => o.messageId === row.id)
          ];
          row.localTime = row.createdAt ? new Date(row.createdAt).toLocaleDateString() + ' ' + new Date(row.createdAt).toLocaleTimeString() : '';
        });
        // Step 4: Save first page and most recent date
        this.data.readMessages = rows;
        if (rows.length > 0) {
          this.mostRecentReadMessageDate = rows[0].createdAt;
        } else {
          this.mostRecentReadMessageDate = new Date().toISOString();
        }
        // Step 5: Filter by readAt and reformat
        const filtered = this.reformat(rows).filter(o =>
          o.ToMessageHeaders && o.ToMessageHeaders[0] && o.ToMessageHeaders[0].readAt !== null
        );
        this.data.readMessages = filtered;
        this.data.totalReadMessages = res?.count || 0;
        this.updatePageDropdown('archived');
      });
  }
  data: any = {
      newMessages: [],
      readMessages: [],
      sentMessages: [],
      totalNewMessages: 0,
      totalReadMessages: 0,
      totalSentMessages: 0
    };

  totalSentMessages = 0;

  showIncoming = true;
  showArchived = false;
  showSent = false;

  pageDropDownMenu: any[] = [];
  selectedPage: any;

  private destroy$ = new Subject<void>();
  private orgIds: number[] = [];
  private numberPerPage = 50;
  organizationsList: any[] = [];
  users: any[] = [];

  private mostRecentSentMessageDate?: string;
  private mostRecentReadMessageDate?: string;
  @Output() openGeofenceCreator = new EventEmitter<void>();

  constructor(
    private messagingService: MessagingService,
    private organizationService: OrganizationService,
    private orgUtil: OrganizationUtilService,
    private modalService: NgbModal,
  ) { }

  ngOnInit(): void {
    this.organizationsList = Object.values(this.orgUtil.myOrgs)
      .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));

    this.orgIds = this.organizationsList.map((org: any) => Number(org.id));
    this.organizationService.getOrgUsers().subscribe(users => {
      this.users = users?.rows ?? []; // Store users in a property
    });

    this.organizationService.getOrganizations()
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.orgIds = (res.rows || []).map((org: any) => org.id);
        this.loadFirstPages();
      });

    // Poll every 5 seconds
    interval(5000).pipe(takeUntil(this.destroy$))
      .subscribe(() => this.pollMessages());
  }

  private loadFirstPages(): void {
    this.loadSentMessages(1, true);
    this.loadReadMessages(1, true);
    this.loadNewMessages();
    // After loading, reset selectedPage to first page
    if (this.pageDropDownMenu.length > 0) {
      this.selectedPage = this.pageDropDownMenu[0];
    }
  }

  private pollMessages(): void {
    this.loadNewMessages();
    // Only poll for the active tab
    if (this.showSent && this.selectedPage) {
      this.loadSentMessages(this.selectedPage.page);
    } else if (this.showArchived && this.selectedPage) {
      this.loadReadMessages(this.selectedPage.page);
    }
  }

  private loadNewMessages(): void {
    // this.messagingService.getNewMessagesForOrgs(this.orgIds)
    //   .pipe(takeUntil(this.destroy$))
    //   .subscribe(res => {
    //     const rows = res?.rows || [];
    //     this.data.newMessages = this.reformat(rows);
    //     this.data.totalNewMessages = res?.count || 0;
    //   });
  }

  private loadSentMessages(page: number, firstPage: boolean = false): void {
    this.messagingService.getSentMessages(this.orgIds, this.numberPerPage, page)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        const rows = res?.rows || [];
        const formatted = this.reformat(rows);
        if (firstPage) {
          this.data.sentMessages = formatted;
          if (formatted.length > 0) this.mostRecentSentMessageDate = formatted[0].createdAt;
          this.updatePageDropdown('sent'); // Only update dropdown on first page load
        } else {
          // Prepend new messages
          this.data.sentMessages = [...formatted, ...this.data.sentMessages].slice(0, this.numberPerPage);
        }
        this.data.totalSentMessages = res?.count || 0;
      });
  }

  private loadReadMessages(page: number, firstPage: boolean = false): void {
    this.messagingService.getReadMessages(this.orgIds, this.numberPerPage, page)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        const rows = res?.rows || [];
        const formatted = this.reformat(rows);
        if (firstPage) {
          this.data.readMessages = formatted;
          if (formatted.length > 0) this.mostRecentReadMessageDate = formatted[0].createdAt;
          this.updatePageDropdown('archived'); // Only update dropdown on first page load
        } else {
          // Prepend new messages
          this.data.readMessages = [...formatted, ...this.data.readMessages].slice(0, this.numberPerPage);
        }
        this.data.totalReadMessages = res?.count || 0;
      });
  }

  private reformat(messages: any[]): any[] {
    return this.orgUtil.reformat(messages, this.users, this.organizationsList);
  }

  setShowIncoming(): void {
    this.showIncoming = true;
    this.showArchived = false;
    this.showSent = false;
  }

  setShowArchived(): void {
  this.showIncoming = false;
  this.showArchived = true;
  this.showSent = false;
  this.updatePageDropdown('archived');
  this.selectedPage = this.pageDropDownMenu[0];
  this.loadReadMessages(1, true);
  }

  setShowSent(): void {
  this.showIncoming = false;
  this.showArchived = false;
  this.showSent = true;
  this.updatePageDropdown('sent');
  this.selectedPage = this.pageDropDownMenu[0];
  this.loadSentMessages(1, true);
  }

  private updatePageDropdown(tab: 'sent' | 'archived'): void {
  let total = tab === 'sent' ? this.data.totalSentMessages : this.data.totalReadMessages;
  let pages = Math.ceil(total / this.numberPerPage);
  const maxPages = 5; // Limit to 5 pages
  const oldSelectedPage = this.selectedPage;
  this.pageDropDownMenu = [];
  for (let i = 0; i < Math.min(pages, maxPages); i++) {
    const start = i * this.numberPerPage + 1;
    const end = Math.min((i + 1) * this.numberPerPage, total);
    this.pageDropDownMenu.push({
      page: i + 1,
      display: `Page ${i + 1} (${start}-${end})`
    });
  }
  // Only reset selectedPage if not set or not in dropdown
  if (!oldSelectedPage || !this.pageDropDownMenu.some(p => p.page === oldSelectedPage.page)) {
    this.selectedPage = this.pageDropDownMenu[0];
  } else {
    this.selectedPage = this.pageDropDownMenu.find(p => p.page === oldSelectedPage.page);
  }
  }

  onPageChange(): void {
    if (!this.selectedPage) return;
    if (this.showSent) this.loadSentMessages(this.selectedPage.page);
    else if (this.showArchived) this.loadReadMessages(this.selectedPage.page);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openMessageDetail(item: any,  isSent: boolean = false): void {
    const modalRef = this.modalService.open(MessageDetailComponent, {
      size: 'lg',
      centered: true,
      backdrop: 'static'
    });
    modalRef.componentInstance.item = item;
    modalRef.componentInstance.isSent = isSent;
  }

    startDrawing(): void {
    this.openGeofenceCreator.emit();
  }
}